import React from "react"
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormHelperText,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import CloudinaryImage from "../../../../components/Image/CloudinaryImage"

const sourceKey = (sourceType, source) => `${sourceType}__${source}`

const getInitials = (label) => (label || "").replace(/[^A-Za-zÀ-ÿ]/g, "").slice(0, 2).toUpperCase()

const sx = {
  logoImage: {
    width: 20,
    height: 20,
    objectFit: "contain",
    flexShrink: 0,
    mr: 1,
  },
  logoWrapper: {
    width: 20,
    height: 20,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    mr: 1,
  },
  logoInitials: {
    width: 20,
    height: 20,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    border: "1px solid #CCCCCC",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontWeight: 500,
    color: "#555555",
    mr: 1,
    flexShrink: 0,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "16px",
  },
  rowWithDelete: {
    gridTemplateColumns: "1fr 1fr 32px",
  },
  deleteBtn: {
    mt: "26px",
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: "#757575",
    mb: 1,
  },
  placeholder: {
    fontSize: 14,
    color: "#9E9E9E",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    py: "4px",
  },
}

const SourceLogo = ({ option }) => {
  if (option.image) {
    return <Box component="img" src={option.image} alt="" sx={sx.logoImage} />
  }

  if (option.logo && option.logo.publicId) {
    return (
      <Box sx={sx.logoWrapper}>
        <CloudinaryImage imageId={option.logo.publicId} width={20} height={20} resize="fit" />
      </Box>
    )
  }

  return <Box component="span" sx={sx.logoInitials}>{getInitials(option.label)}</Box>
}

const CatalogSourceRow = ({
  row,
  sourceOptions,
  showDelete,
  submitted,
  isSubmitting,
  isDuplicate,
  lockedProductTypes,
  isMergeSource,
  onSourceChange,
  onProductTypesChange,
  onRemove,
}) => {
  const selectedOption = sourceOptions.find(o => sourceKey(o.sourceType, o.source) === row.sourceKey)
  const noSourceSelected = !row.sourceKey
  const newProductTypesCount = row.productTypes.filter(pt => !lockedProductTypes.includes(pt)).length
  const noProductTypeSelected = isMergeSource ? newProductTypesCount === 0 : row.productTypes.length === 0

  return (
    <Box sx={{ ...sx.row, ...(showDelete ? sx.rowWithDelete : {}) }}>
      <Box>
        <Typography sx={sx.fieldLabel}>Source</Typography>
        <Select
          fullWidth
          variant="standard"
          displayEmpty
          value={row.sourceKey}
          onChange={(e) => onSourceChange(e.target.value)}
        >
          <MenuItem value="" disabled>
            <Typography sx={sx.placeholder}>Sélectionner une marque ou une entreprise</Typography>
          </MenuItem>
          {sourceOptions.map(o => (
            <MenuItem key={sourceKey(o.sourceType, o.source)} value={sourceKey(o.sourceType, o.source)}>
              <SourceLogo option={o} />
              {o.label}
            </MenuItem>
          ))}
        </Select>
        {isDuplicate && (
          <FormHelperText error>Cette source est déjà sélectionnée ci-dessus.</FormHelperText>
        )}
        {submitted && !isDuplicate && noSourceSelected && (
          <FormHelperText error>Veuillez sélectionner une marque ou entreprise source.</FormHelperText>
        )}
      </Box>

      <Box>
        <Typography sx={sx.fieldLabel}>Types de produits</Typography>
        {row.loadingTypes ? (
          <CircularProgress size={20} />
        ) : (
          <Select
            fullWidth
            multiple
            variant="standard"
            displayEmpty
            value={row.productTypes}
            onChange={(e) => onProductTypesChange(e.target.value)}
            disabled={!selectedOption || row.availableProductTypes.length === 0}
            MenuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              PaperProps: { style: { maxHeight: 380 } },
            }}
            renderValue={(values) => values.length === 0 ? (
              <Typography sx={sx.placeholder}>Sélectionner des types de produits</Typography>
            ) : (
              <Box sx={sx.chips}>
                {row.availableProductTypes.filter(t => values.includes(t.value)).map(t => (
                  <Chip key={t.value} label={t.label} size="small" />
                ))}
              </Box>
            )}
          >
            {row.availableProductTypes.map(t => {
              const isSelected = row.productTypes.includes(t.value)
              const isLocked = isSelected && lockedProductTypes.includes(t.value)
              return (
                <MenuItem key={t.value} value={t.value} disabled={isLocked}>
                  <Checkbox checked={isSelected} disabled={isLocked} />
                  <ListItemText
                    primary={t.label}
                    secondary={isLocked ? "Déjà incluse" : undefined}
                  />
                </MenuItem>
              )
            })}
          </Select>
        )}
        {selectedOption && !row.loadingTypes && row.availableProductTypes.length === 0 && (
          <FormHelperText>Aucun type de produit disponible pour cette source.</FormHelperText>
        )}
        {submitted && !noSourceSelected && noProductTypeSelected && (
          <FormHelperText error>
            {isMergeSource ? "Sélectionnez au moins une catégorie à ajouter." : "Sélectionnez au moins une catégorie de recettes"}
          </FormHelperText>
        )}
      </Box>

      {showDelete && (
        <IconButton
          size="small"
          sx={sx.deleteBtn}
          onClick={onRemove}
          disabled={isSubmitting}
          aria-label="Retirer cette source"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  )
}

export default CatalogSourceRow
